import { useSelector, useDispatch } from 'react-redux';
import {
  signInWithPopup, signOut,
  createUserWithEmailAndPassword, signInWithEmailAndPassword,
  sendPasswordResetEmail, updateProfile, sendEmailVerification,
} from 'firebase/auth';
import { auth, googleProvider } from '../config/firebase';
import {
  setUser, clearUser,
  selectUser, selectRole, selectIsAdmin, selectIsSuperAdmin, selectIsManager, selectModulePermissions
} from '../app/authSlice';
import { userPermissionsService, usersService } from '../services/db';
import { ROLES } from '../constants';

/**
 * useAuth — centralizes all authentication logic.
 *
 * Usage:
 *   const { user, role, isAdmin, isSuperAdmin, permissions, signIn, logout } = useAuth();
 */
export const useAuth = () => {
  const dispatch     = useDispatch();
  const user         = useSelector(selectUser);
  const role         = useSelector(selectRole);
  const isAdmin      = useSelector(selectIsAdmin);
  const isSuperAdmin = useSelector(selectIsSuperAdmin);
  const isManager    = useSelector(selectIsManager);
  const permissions  = useSelector(selectModulePermissions);

  const finalizeSignIn = async (firebaseUser, displayNameOverride) => {
    const userPerm = await userPermissionsService.getPermissions(
      firebaseUser.uid,
      firebaseUser.email
    );

    const displayName = displayNameOverride ?? firebaseUser.displayName;

    dispatch(setUser({
      user: {
        uid:         firebaseUser.uid,
        email:       firebaseUser.email,
        displayName: displayName,
        photoURL:    firebaseUser.photoURL,
      },
      role: userPerm.role || ROLES.user,
      isSuperAdmin: Boolean(userPerm.isSuperAdmin),
      permissions: userPerm.modulePermissions,
    }));

    // Ensure users/{userId} profile document exists and has profile attributes
    usersService.getUserProfile(firebaseUser.uid, {
      uid:         firebaseUser.uid,
      displayName: displayName,
      email:       firebaseUser.email,
      photoURL:    firebaseUser.photoURL,
    }).catch((err) => console.warn('Could not sync user profile on login:', err));

    return { user: firebaseUser, role: userPerm.role, permissions: userPerm.modulePermissions };
  };

  const signIn = async () => {
    const result = await signInWithPopup(auth, googleProvider);
    return finalizeSignIn(result.user);
  };

  const signInWithEmail = async (email, password) => {
    const result = await signInWithEmailAndPassword(auth, email, password);
    return finalizeSignIn(result.user);
  };

  const signUpWithEmail = async (email, password, displayName) => {
    const result = await createUserWithEmailAndPassword(auth, email, password);
    await updateProfile(result.user, { displayName });
    sendEmailVerification(result.user).catch(() => {});
    return finalizeSignIn(result.user, displayName);
  };

  const resetPassword = (email) => sendPasswordResetEmail(auth, email);

  const logout = async () => {
    await signOut(auth);
    dispatch(clearUser());
    // Clear navigation state
    localStorage.removeItem('selected_season');
    localStorage.removeItem('selected_quiz');
    localStorage.removeItem('selected_quiz_details');
    localStorage.removeItem('selected_question');
    localStorage.removeItem('selected_season_to_update');
  };

  return {
    user,
    role,
    isAdmin,
    isSuperAdmin,
    permissions,
    isManager,
    signIn,
    signInWithEmail,
    signUpWithEmail,
    resetPassword,
    logout
  };
};
